import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Autocomplete from "veneer/AutocompleteSelectInput";
import { MultiTextInput } from "veneer/MultiTextInput";
import Paper from "veneer/Paper";
import Stack from "veneer/Stack";
import Switch from "veneer/Switch";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { useFeatureFlag } from "cortex/ui/src/hooks";
import type { NumberOption, StringOption } from "cortex/ui/src/ui/table/types";

import {
  ALERT_PRIORITY_OPTIONS,
  FormErrors,
  FormValues,
  PRIORITY_OPTIONS,
} from "../model";

type SourceOption = {
  isDisabled?: boolean;
  label: string;
  value: string | number;
};

type AlertFormProps = {
  errors?: FormErrors;
  eventNameOptions: StringOption[];
  onChange: (next: FormValues) => void;
  onPresetChange: (opt: StringOption | null) => void;
  presetOptions: Array<StringOption & { group: string }>;
  presetState: StringOption | null;
  setErrors?: Dispatch<SetStateAction<FormErrors>>;
  sourceOptions: SourceOption[];
  targetOpts: StringOption[];
  users: ReadonlyArray<{ id: number; name: string; email: string }>;
  value: FormValues;
};

const noopSetErrors: Dispatch<SetStateAction<FormErrors>> = () => {};

export default function AlertForm({
  errors = {},
  eventNameOptions,
  onChange,
  onPresetChange,
  presetOptions,
  presetState,
  setErrors = noopSetErrors,
  sourceOptions,
  targetOpts,
  users,
  value,
}: AlertFormProps) {
  const isGroupedTriggeredAlertsEnabled = useFeatureFlag(
    "is-grouped-triggered-alerts",
  );

  const clearError = useCallback(
    (k: keyof FormValues) => {
      setErrors((prev) => {
        const { [k]: _omit, ...rest } = prev;
        return rest;
      });
    },
    [setErrors],
  );

  return (
    <Stack spacing={2}>
      <Autocomplete
        getOptionKey={(option: StringOption) => option.value}
        groupBy={(opt) => (opt as any).group}
        inputProps={{
          label: "Alert",
          placeholder: "Select from predefined alerts or create custom",
        }}
        onChange={(_e, opt: StringOption | null) => onPresetChange(opt)}
        options={presetOptions}
        size="small"
        value={presetState}
      />
      <TextInput
        bgcolor={Colors.white}
        error={Boolean(errors.name)}
        fullWidth
        helperText={errors.name ?? undefined}
        label="Name"
        onChange={(newValue) => {
          onChange({ ...value, name: newValue ?? "" });
          if (errors.name) clearError("name");
        }}
        placeholder="Enter the name of the alert"
        required
        value={value.name}
      />
      {isGroupedTriggeredAlertsEnabled && (
        <>
          <Autocomplete
            dataTestId="alert-priority"
            inputProps={{
              label: "Severity",
              placeholder: "Select severity",
            }}
            onChange={(_event, option: StringOption | null) =>
              onChange({
                ...value,
                priority:
                  option?.value === "info" ||
                  option?.value === "warning" ||
                  option?.value === "critical"
                    ? option.value
                    : "info",
              })
            }
            options={ALERT_PRIORITY_OPTIONS}
            size="small"
            value={
              ALERT_PRIORITY_OPTIONS.find(
                (option) => option.value === value.priority,
              ) ?? null
            }
          />
          <MultiTextInput
            dataTestId="alert-description"
            fullWidth
            label="Description"
            minRows={3}
            onChange={(newValue) =>
              onChange({
                ...value,
                description: newValue ?? "",
              })
            }
            placeholder="Describe the alert"
            value={value.description ?? ""}
          />
        </>
      )}
      <Stack alignItems="center" direction="row" spacing={2}>
        <Switch
          checked={value.createResolvableIncident}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({
              ...value,
              createResolvableIncident: event.target.checked,
            })
          }
        />
        <Typography color={Colors.gray1000}>
          Also create triggered alerts
        </Typography>
      </Stack>
      <Typography color={Colors.gray1000}>
        If checked, when an alert is created, we will also create a resolvable
        triggered alert to track issue resolution.
        <br />
        The triggered alert will be viewable on Fleet under{" "}
        <Link to="/diagnostics/incidents">Triggered Alerts</Link>.
      </Typography>
      <Typography color={Colors.gray1000}>
        Otherwise, we will only send an alert.
      </Typography>
      <FormWrapper>
        <Autocomplete
          inputProps={{
            label: "Event Name",
            placeholder: "Select or create event name",
          }}
          multiple
          onChange={(_e, opts: StringOption[] | null) =>
            onChange({
              ...value,
              eventNames: opts ? opts.map((o) => o.value) : [],
            })
          }
          options={eventNameOptions}
          size="small"
          value={
            value.eventNames
              ? eventNameOptions.filter((o) =>
                  value.eventNames?.includes(o.value),
                )
              : []
          }
        />
        <Stack alignItems="center" direction="row" spacing={2}>
          <Autocomplete
            getOptionDisabled={(option) => (option as any).isDisabled}
            inputProps={{
              label: "Source",
              placeholder: "e.g. Application or Player",
            }}
            multiple
            onChange={(_e, opts: SourceOption[] | null) =>
              onChange({
                ...value,
                sources: opts ? opts.map((o) => o.value) : [],
              })
            }
            options={sourceOptions}
            size="small"
            value={
              value.sources
                ? sourceOptions.filter((o) => value.sources?.includes(o.value))
                : []
            }
          />
          <Autocomplete
            inputProps={{
              label: "Priority",
            }}
            multiple
            onChange={(_e, opts: NumberOption[]) =>
              onChange({ ...value, priorities: opts.map((o) => o.value) })
            }
            options={PRIORITY_OPTIONS}
            size="small"
            value={
              value.priorities
                ? PRIORITY_OPTIONS.filter((o) =>
                    value.priorities?.includes(o.value),
                  )
                : []
            }
          />
        </Stack>
        <Stack alignItems="center" direction="row" spacing={2}>
          <TextInput
            error={Boolean(errors.frequency)}
            fullWidth
            helperText={errors.frequency ?? undefined}
            label="Frequency of Events"
            onChange={(newValue) => {
              onChange({
                ...value,
                frequency: newValue === "" ? 0 : Number(newValue),
              });
              if (errors.frequency) clearError("frequency");
            }}
            value={String(value.frequency)}
          />
          <TextInput
            endAdornment="min"
            error={Boolean(errors.threshold)}
            fullWidth
            helperText={errors.threshold ?? undefined}
            label="Threshold"
            onChange={(newValue) => {
              onChange({
                ...value,
                threshold: newValue === "" ? 0 : Number(newValue),
              });
              if (errors.threshold) clearError("threshold");
            }}
            value={String(value.threshold)}
          />
        </Stack>
      </FormWrapper>
      <Stack alignItems="center" direction="row" spacing={2}>
        <Autocomplete
          getOptionDisabled={(option) => (option as any).isDisabled}
          inputProps={{
            error: Boolean(errors.targetDevices),
            helperText: errors.targetDevices ?? undefined,
            label: "Target Devices",
            placeholder: "e.g. Application or Player",
            required: true,
          }}
          multiple
          onChange={(_e, opt: StringOption[] | null) => {
            onChange({
              ...value,
              targetDevices: opt ? opt.map((o) => o.value) : [],
            });
            if (errors.targetDevices) clearError("targetDevices");
          }}
          options={targetOpts}
          size="small"
          value={
            value.targetDevices
              ? targetOpts.filter((o) => value.targetDevices?.includes(o.value))
              : []
          }
        />

        <Autocomplete
          inputProps={{
            error: Boolean(errors.emailAddresses),
            helperText: errors.emailAddresses ?? undefined,
            label: "Email Adresses",
            placeholder: "Select from user email addresses",
            required: true,
          }}
          multiple
          onChange={(_e, opts: NumberOption[]) => {
            onChange({ ...value, emailAddresses: opts.map((o) => o.value) });
            if (errors.emailAddresses) clearError("emailAddresses");
          }}
          options={
            users.map((u) => ({
              label: `${u.name} <${u.email}>`,
              value: u.id,
            })) as NumberOption[]
          }
          size="small"
          value={
            value.emailAddresses
              ? users
                  .filter((u) => value.emailAddresses?.includes(u.id))
                  .map((u) => ({
                    label: `${u.name} <${u.email}>`,
                    value: u.id,
                  }))
              : []
          }
        />
      </Stack>
      {value.createResolvableIncident && (
        <Alert dense severity="info">
          We will create an incident when these rules are matched.
        </Alert>
      )}
    </Stack>
  );
}

const FormWrapper = styled(Paper)(({ theme }) => ({
  backgroundColor: Colors.gray100,
  borderRadius: theme.shape.borderRadius * 2,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  "& .MuiInputBase-root": { backgroundColor: Colors.white },
}));
